// AR Report — Per-attempt list page.
//
// Lets ops drill into every individual AR attempt for one cohort × pub,
// filterable by attempt seq / status / failure category / retry path /
// date. Each row is clickable to the per-attempt detail page.

import { useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, type ARAttemptList } from '../api/client'
import { Hint, StatusBadge, RetryPathBadge, FailureCategoryBadge } from '../components/ARGlossary'

const PAGE_SIZE = 100

const ATTEMPT_SEQS = [
  { v: '', label: 'All attempts' },
  { v: '1', label: 'Attempt 1' },
  { v: '2', label: 'Attempt 2' },
  { v: '3', label: 'Attempt 3' },
  { v: '4', label: 'Attempt 4' },
  { v: '5', label: 'Attempt 5+' },
]

const STATUSES = [
  { v: '', label: 'All statuses' },
  { v: 'CHARGED', label: 'CHARGED' },
  { v: 'CARD UPDATED', label: 'CARD UPDATED' },
  { v: 'FIRST CHARGE FAILED', label: 'FIRST CHARGE FAILED (standard path)' },
  { v: 'CHARGE FAILED', label: 'CHARGE FAILED (fast-retry path)' },
  { v: 'RECHARGE FAILED', label: 'RECHARGE FAILED' },
  { v: 'THIRD CHARGE FAILED', label: 'THIRD CHARGE FAILED' },
  { v: 'FOURTH CHARGE FAILED', label: 'FOURTH CHARGE FAILED' },
  { v: 'FIFTH CHARGE FAILED', label: 'FIFTH CHARGE FAILED' },
]

const RETRY_PATHS = [
  { v: '', label: 'Any path' },
  { v: 'fast', label: '⚡ Fast-retry only' },
  { v: 'standard', label: 'Standard cadence only' },
]

const FAILURE_CATS = [
  { v: '', label: 'Any reason' },
  { v: 'invalid_card_closed', label: 'Invalid card (closed/replaced)' },
  { v: 'invalid_card_expired', label: 'Card expired' },
  { v: 'no_payment_profile', label: 'No card on file' },
  { v: 'declined', label: 'Declined / fraud' },
  { v: 'insufficient_funds', label: 'Insufficient funds' },
  { v: 'avs_mismatch', label: 'AVS mismatch' },
  { v: 'card_type_unsupported', label: 'Card type unsupported' },
  { v: 'system_error', label: 'System / gateway error' },
  { v: 'other', label: 'Other / unclassified' },
]

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function monthLabel(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z')
  return d.toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

export function ARReportAttemptsPage() {
  const { month, publication } = useParams<{ month: string; publication: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const [page, setPage] = useState(0)

  const cohortMonth = month ? `${month}-01` : ''
  const filters = {
    attempt_seq: searchParams.get('attempt_seq') || '',
    status: searchParams.get('status') || '',
    retry_path: searchParams.get('retry_path') || '',
    failure_category: searchParams.get('failure_category') || '',
    on_date: searchParams.get('on_date') || '',
  }

  const updateFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams)
    if (value) next.set(key, value)
    else next.delete(key)
    setSearchParams(next)
    setPage(0)
  }

  const params = new URLSearchParams({
    limit: String(PAGE_SIZE),
    offset: String(page * PAGE_SIZE),
  })
  for (const [k, v] of Object.entries(filters)) if (v) params.set(k, v)

  const { data, isLoading } = useQuery({
    queryKey: ['ar-attempts', cohortMonth, publication, filters, page],
    queryFn: () => api.get<ARAttemptList>(
      `/api/ar-report/cohort/${cohortMonth}/${publication}/attempts?${params.toString()}`,
    ),
    enabled: !!cohortMonth && !!publication,
  })

  return (
    <div className="p-8 space-y-6 max-w-[1500px]">
      <div className="flex items-center gap-3 flex-wrap">
        <Link to="/ar-report" className="label-amber hover:underline">← AR Report</Link>
        <span className="label text-paper-trace">/</span>
        <Link to={`/ar-report/${month}/${publication}`} className="label-amber hover:underline">
          {month && monthLabel(`${month}-01`)} · {publication?.replace(/_/g, ' ')}
        </Link>
        <span className="label text-paper-trace">/</span>
        <span className="label">Attempts</span>
      </div>

      <header className="border-b border-ink-500 pb-4">
        <div className="label mb-1">Per-attempt drill-down</div>
        <h1 className="font-serif text-[28px] leading-tight text-paper">
          Every AR attempt for this cohort
        </h1>
        <p className="text-paper-dim text-[13px] mt-2 max-w-[800px]">
          Each row is one charge attempt SC made. Filter by attempt number,
          status, retry path (fast vs standard), or failure reason to slice
          the cohort. Click any row to see the full attempt detail with
          surrounding context and SC's raw report row.
        </p>
      </header>

      {/* Filters */}
      <div className="border border-ink-500 bg-ink-800/40 p-4 space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <FilterSelect
            label="Attempt seq"
            value={filters.attempt_seq}
            onChange={(v) => updateFilter('attempt_seq', v)}
            options={ATTEMPT_SEQS}
            hint={{
              title: 'Attempt sequence number',
              short: 'Which attempt-in-cycle this row is. A1 = first scheduled charge; A2 = first retry; A5 = final.',
              detail: 'For invalid_card_closed subs on the fast-retry path, the +22h bonus retry counts as A2 — so A2 has both fast-path recoveries AND standard-path scheduled retries mixed in.',
            }}
          />
          <FilterSelect
            label="Status"
            value={filters.status}
            onChange={(v) => updateFilter('status', v)}
            options={STATUSES}
          />
          <FilterSelect
            label="Retry path"
            value={filters.retry_path}
            onChange={(v) => updateFilter('retry_path', v)}
            options={RETRY_PATHS}
            hint={{
              title: 'Retry path',
              short: 'Which SC cadence the failure went on.',
              detail: '⚡ Fast = SC labels with bare "CHARGE FAILED" (no FIRST prefix); applies to invalid-card-number failures only. SC sends an update-card email and re-attempts ~22h later. Standard = "FIRST CHARGE FAILED"; uses the +5/+3/+8/+6 day cadence with no fast-retry.',
            }}
          />
          <FilterSelect
            label="Failure reason"
            value={filters.failure_category}
            onChange={(v) => updateFilter('failure_category', v)}
            options={FAILURE_CATS}
          />
          <FilterSelect
            label="On date"
            value={filters.on_date}
            onChange={(v) => updateFilter('on_date', v)}
            type="date"
          />
        </div>
        <div className="flex items-center justify-between">
          <div className="text-[11px] text-paper-trace font-mono">
            {isLoading ? 'loading…' : `${data?.total ?? 0} attempts match`}
          </div>
          {(filters.attempt_seq || filters.status || filters.retry_path || filters.failure_category || filters.on_date) && (
            <button
              className="label-amber text-[11px] hover:underline"
              onClick={() => setSearchParams(new URLSearchParams())}
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Results table */}
      <div className="border border-ink-500 bg-ink-800/40 overflow-hidden">
        <table className="w-full text-[12px] font-mono">
          <thead>
            <tr className="border-b border-ink-500/60 bg-ink-800/60">
              <th className="text-left px-3 py-2 label font-normal">When</th>
              <th className="text-center px-3 py-2 label font-normal">A#</th>
              <th className="text-left px-3 py-2 label font-normal">Status</th>
              <th className="text-left px-3 py-2 label font-normal">Path</th>
              <th className="text-left px-3 py-2 label font-normal">Reason</th>
              <th className="text-left px-3 py-2 label font-normal">Subscriber</th>
              <th className="text-right px-3 py-2 label font-normal">$</th>
              <th className="text-left px-3 py-2 label font-normal">Details</th>
            </tr>
          </thead>
          <tbody>
            {(data?.items ?? []).map((it) => (
              <tr
                key={it.attempt_id}
                className="border-b border-ink-500/20 hover:bg-ink-700/30 transition-colors"
              >
                <td className="px-3 py-2 align-top whitespace-nowrap text-paper-dim">
                  <Link
                    to={`/ar-report/attempt/${it.attempt_id}`}
                    className="hover:text-amber-glow"
                  >
                    {fmtDateTime(it.attempt_at)}
                  </Link>
                </td>
                <td className="px-3 py-2 align-top text-center text-paper">{it.attempt_seq}</td>
                <td className="px-3 py-2 align-top"><StatusBadge status={it.status} /></td>
                <td className="px-3 py-2 align-top"><RetryPathBadge path={it.retry_path} /></td>
                <td className="px-3 py-2 align-top"><FailureCategoryBadge category={it.failure_category} /></td>
                <td className="px-3 py-2 align-top text-paper-dim">
                  <Link to={`/ar-report/attempt/${it.attempt_id}`} className="hover:text-amber-glow">
                    {it.email ?? <span className="text-paper-trace italic">no email</span>}
                  </Link>
                  <div className="text-paper-trace text-[10px]">
                    #{it.simplecirc_account_id} · sub#{it.subscription_id}
                  </div>
                </td>
                <td className="px-3 py-2 align-top text-right tabular-nums text-paper">
                  {it.paid_amount != null ? `$${it.paid_amount.toFixed(2)}` : '—'}
                </td>
                <td className="px-3 py-2 align-top text-paper-dim text-[11px] max-w-[280px] truncate" title={it.details ?? ''}>
                  {it.details ?? <span className="text-paper-trace">—</span>}
                </td>
              </tr>
            ))}
            {(!data || data.items.length === 0) && !isLoading && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-paper-trace label">
                  No attempts match the current filters
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-[12px] font-mono">
          <span className="text-paper-trace">
            Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, data.total)} of {data.total}
          </span>
          <div className="flex gap-2">
            <button
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
              className="px-3 py-1 border border-ink-500 disabled:opacity-30 hover:border-amber-glow"
            >
              ← prev
            </button>
            <button
              disabled={(page + 1) * PAGE_SIZE >= data.total}
              onClick={() => setPage(page + 1)}
              className="px-3 py-1 border border-ink-500 disabled:opacity-30 hover:border-amber-glow"
            >
              next →
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

type FilterOption = { v: string; label: string }
type FilterHintProps = { title?: string; short?: string; detail?: string }

function FilterSelect({
  label, value, onChange, options, type = 'select', hint,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  options?: FilterOption[]
  type?: 'select' | 'date'
  hint?: FilterHintProps
}) {
  return (
    <label className="block">
      <span className="label text-paper-faint mb-1 inline-flex items-baseline gap-1">
        {hint ? <Hint {...hint}><span>{label}</span></Hint> : <span>{label}</span>}
      </span>
      {type === 'select' ? (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-ink-700 border border-ink-500 px-2 py-1.5 text-[12px] text-paper font-mono"
        >
          {(options ?? []).map((o) => (
            <option key={o.v} value={o.v}>{o.label}</option>
          ))}
        </select>
      ) : (
        <input
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-ink-700 border border-ink-500 px-2 py-1.5 text-[12px] text-paper font-mono"
        />
      )}
    </label>
  )
}
